import { useCallback } from 'react';
import { useFiltersStore } from '../store/filters-store';
import { buildMovieApiParams, buildTVApiParams } from '../builders';
import { YEAR_CONSTRAINTS, VOTE_COUNT_THRESHOLDS } from '../constants';

const MIN_YEAR = YEAR_CONSTRAINTS.MIN_YEAR;
const CURRENT_YEAR = YEAR_CONSTRAINTS.CURRENT_YEAR;

const validateDateRange = (fromYear, toYear) => {
  if (!fromYear && !toYear) {
    return { fromYear, toYear };
  }

  let validFromYear = fromYear;
  let validToYear = toYear;

  if (fromYear) {
    const from = parseInt(fromYear);
    if (from < MIN_YEAR || from > CURRENT_YEAR) {
      validFromYear = '';
    }
  }

  if (toYear) {
    const to = parseInt(toYear);
    if (to < MIN_YEAR || to > CURRENT_YEAR) {
      validToYear = '';
    }

    if (validFromYear && validToYear && parseInt(validToYear) < parseInt(validFromYear)) {
      validToYear = '';
    }
  }

  return { fromYear: validFromYear, toYear: validToYear };
};

function useUnifiedFilters(initialFilters = {}, pageType = 'movie') {
  // Get filters from Zustand store
  const filters = useFiltersStore((state) => state.sharedFilters);
  const setFilters = useFiltersStore((state) => state.setFilters);
  const resetFilters = useFiltersStore((state) => state.resetFilters);
  const hasActiveFilters = useFiltersStore((state) => state.hasActiveFilters());

  const updateFilter = useCallback((key, value) => {
    if (key === 'fromYear' || key === 'toYear') {
      const combined = {
        fromYear: key === 'fromYear' ? value : filters.fromYear,
        toYear: key === 'toYear' ? value : filters.toYear
      };
      const validatedDates = validateDateRange(combined.fromYear, combined.toYear);
      
      // Build update object with only necessary fields
      const updatedFields = {
        [key]: validatedDates[key]
      };
      
      // Only update the other field if validation actually changed it
      if (key === 'fromYear' && validatedDates.toYear !== combined.toYear) {
        updatedFields.toYear = validatedDates.toYear;
      }
      if (key === 'toYear' && validatedDates.fromYear !== combined.fromYear) {
        updatedFields.fromYear = validatedDates.fromYear;
      }
      
      setFilters(updatedFields);
    } else {
      setFilters({ [key]: value });
    }
  }, [filters.fromYear, filters.toYear, setFilters]);

  const updateFilters = useCallback((newFilters) => {
    if (newFilters.fromYear !== undefined || newFilters.toYear !== undefined) {
      const combined = { ...filters, ...newFilters };
      const validatedDates = validateDateRange(combined.fromYear, combined.toYear);
      
      // Build update object with only the fields that should change
      const updatedFields = { ...newFilters };
      
      // Apply validated dates
      if (newFilters.fromYear !== undefined) {
        updatedFields.fromYear = validatedDates.fromYear;
      }
      if (newFilters.toYear !== undefined) {
        updatedFields.toYear = validatedDates.toYear;
      }
      
      // If validation cleared the other field, include that change too
      if (newFilters.fromYear !== undefined && !newFilters.toYear && validatedDates.toYear !== combined.toYear) {
        updatedFields.toYear = validatedDates.toYear;
      }
      if (newFilters.toYear !== undefined && !newFilters.fromYear && validatedDates.fromYear !== combined.fromYear) {
        updatedFields.fromYear = validatedDates.fromYear;
      }
      
      setFilters(updatedFields);
    } else {
      setFilters(newFilters);
    }
  }, [filters, setFilters]);

  const filterResults = useCallback((results, searchQuery = null) => {
    if (!Array.isArray(results)) return results;

    return results.filter(item => {
      if (!searchQuery?.trim()) {
        const tmdbVoteCount = item.vote_count || 0;
        const imdbVoteCount = item.enhanced_ratings?.real_ratings?.imdb?.votes || 0;
        
        if (tmdbVoteCount < VOTE_COUNT_THRESHOLDS.MIN_QUALITY_VOTES && imdbVoteCount < VOTE_COUNT_THRESHOLDS.MIN_QUALITY_VOTES) {
          return false;
        }
      }

      return true;
    });
  }, []);

  return {
    filters,
    updateFilter,
    updateFilters,
    resetFilters,
    hasActiveFilters,
    getMovieApiParams: () => buildMovieApiParams(filters),
    getTVApiParams: () => buildTVApiParams(filters),
    filterResults,
    
    // Backward compatibility
    getApiParams: () => buildMovieApiParams(filters),
  };
}

export default useUnifiedFilters; 